/**
 * Translation Resource Loader
 * Dynamically imports JSON translation files
 * @module @infra/i18n
 */

import { Language, Namespace, TranslationResource } from './i18n.types';

// Glob import - auto-discovers all translation files
// Vite chunks these into separate i18n-en.js and i18n-vi.js files
const translationModules = import.meta.glob<{ default: TranslationResource }>(
  './locales/*/[a-z-]*.json',
  { eager: false }
);

// Base namespaces are bundled EAGERLY into the main chunk (not a lazy i18n chunk) so the
// critical always-mounted UI — error boundaries, the session-revoked auth modal, landing —
// can always render translated text. Without this, a failed LATER chunk load surfaces the
// error card with raw keys (`common:messages.error`), since the fallback copy itself lives
// in `common` and would otherwise be lazy too. Keep in sync with `baseNamespaces` in i18n.ts.
//
// Only `vi` and `en`, not every language: a glob is resolved at build time, so `*` would put
// all six in the main chunk and make a Vietnamese-only user download Khmer and Burmese
// (three bytes per character) they will never read. The guarantee above survives because
// `I18N_DEFAULT_LANGUAGE` is always one of these two and the service leans on it twice —
// `setLanguage` preloads the default's base namespaces alongside the chosen language, and a
// key missing from the active language falls back to the default. So the worst case for a
// Khmer reader whose chunk fails is an error screen in Vietnamese, not one in raw keys.
//
// `en` is here because it IS `I18N_DEFAULT_LANGUAGE` — the one language every other
// language leans on, so it has to be present no matter who is looking. `vi` is here for a
// weaker reason: it is not a fallback layer (the service only ever falls back to the
// default), just the second language with real content, so most sessions would fetch it
// anyway. Change `I18N_DEFAULT_LANGUAGE` and this list changes in the same commit; drop
// `vi` from it and nothing breaks, Vietnamese readers simply pay one extra request.
const eagerBaseTranslations = import.meta.glob<{ default: TranslationResource }>(
  './locales/{en,vi}/{common,landing,errors,auth,emailVerification}.json',
  { eager: true }
);

/**
 * Dynamically import translation JSON file using glob imports
 * JSON files are chunked into separate i18n language files
 */
async function importTranslation(
  language: Language,
  namespace: Namespace
): Promise<TranslationResource> {
  const path = `./locales/${language}/${namespace}.json`;

  // Base namespaces are pre-bundled — resolve them synchronously so they can NEVER fail a
  // network fetch, guaranteeing translated fallback copy even when other chunks are stale.
  const eager = eagerBaseTranslations[path];
  if (eager) {
    return eager.default || eager;
  }

  const moduleLoader = translationModules[path];

  // Not in the build glob (missing file / bad namespace) — a retry won't help, so degrade
  // gracefully to empty instead of throwing.
  if (!moduleLoader) {
    console.warn(`Translation module not found: ${language}/${namespace}`);
    return {};
  }

  // Let a load rejection (network / chunk-load failure) PROPAGATE so the caller can retry.
  // Swallowing it here would mark the namespace "loaded" (empty) → raw keys app-wide until a
  // manual reload, and would defeat both the Router and the boot retry loops.
  const module = await moduleLoader();
  return module.default || module;
}

/**
 * Load specific namespaces for a language
 */
export async function loadNamespaceTranslations(
  language: Language,
  namespaces: Namespace[]
): Promise<Record<Namespace, TranslationResource>> {
  const translations = await Promise.all(
    namespaces.map(async (ns) => {
      const resource = await importTranslation(language, ns);
      return [ns, resource] as const;
    })
  );

  return Object.fromEntries(translations) as Record<Namespace, TranslationResource>;
}

/**
 * Get nested value from translation object
 */
export function getNestedValue(obj: TranslationResource, path: string): string | undefined {
  const keys = path.split('.');
  let current: string | TranslationResource = obj;

  for (const key of keys) {
    if (typeof current === 'object' && current !== null && key in current) {
      current = (current as Record<string, string | TranslationResource>)[key];
    } else {
      return undefined;
    }
  }

  return typeof current === 'string' ? current : undefined;
}

/**
 * Interpolate parameters into translation string
 * Replaces {{paramKey}} with param values
 */
export function interpolateParams(text: string, params?: Record<string, string | number>): string {
  if (!params || Object.keys(params).length === 0) {
    return text;
  }

  let result = text;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(new RegExp(`{{${key}}}`, 'g'), () => String(value));
  }
  return result;
}
